import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';
import { createTray } from '@/js/core/utils/trays-api';
import { useTraysListStore } from '@/js/core/stores/trays-list-store';
import { useUserStore } from '@/js/core/stores/user-store';
import { trayAccessPayload, validateManagerAccess } from '@/js/core/utils/tray-access';
import { isRichTextEmpty } from '@/js/core/utils/rich-text';
import { trayRouteParams } from '@/js/core/utils/tray-route';
import { scrollToFirstFormError } from '@/js/core/utils/scroll-to-error';

/** Require a non-empty description. */
function validateTrayDescription(description = '') {
  return isRichTextEmpty(description) ? { content: 'Description is required' } : {};
}

/** Require alt text once a featured image has been chosen. */
function validateFeaturedMediaAlt({ imageUrl, alt }) {
  if (imageUrl && !alt.trim()) {
    return { featured_media_alt: 'Alt text is required for featured media' };
  }

  return {};
}

export function useCreateTray(media) {
  const router = useRouter();
  const traysStore = useTraysListStore();
  const userStore = useUserStore();

  const form = ref({
    title: '',
    visibleTo: 'everyone',
    commentAccess: 'viewers',
    visibleUsers: [],
    commentableUsers: [],
    managerAccess: 'owner',
    managerUsers: [],
  });

  const isSubmitting = ref(false);
  const errorMessage = ref('');
  const fieldErrors = ref({});
  const editorRef = ref(null);

  // Prevent the user from adding themselves as a manager. Handles guest case where userStore.uuid can be null/undefined.
  const excludedManagerUuids = computed(() => [userStore.uuid].filter(Boolean));

  // Managers hold access by role, so they are listed in the access fields rather
  // than selected there. Empty unless editing is restricted to specific people.
  const selectedManagerUsers = computed(() =>
    form.value.managerAccess === 'specific' ? form.value.managerUsers : []
  );

  const handleMediaAltChange = value => {
    if (value.trim() && fieldErrors.value.featured_media_alt) {
      const remainingErrors = { ...fieldErrors.value };
      delete remainingErrors.featured_media_alt;
      fieldErrors.value = remainingErrors;
    }
  };

  const handleSubmit = async () => {
    isSubmitting.value = true;
    errorMessage.value = '';

    const description = editorRef.value?.getContent() || '';

    fieldErrors.value = {
      ...validateManagerAccess(form.value),
      ...validateTrayDescription(description),
      ...validateFeaturedMediaAlt({ imageUrl: media.imageUrl.value, alt: media.alt.value }),
    };

    if (Object.keys(fieldErrors.value).length > 0) {
      isSubmitting.value = false;
      scrollToFirstFormError('#tray-form');
      return;
    }

    const managerUserUuids =
      form.value.managerAccess === 'specific' ? form.value.managerUsers.map(user => user.uuid) : [];

    try {
      const response = await createTray({
        title: form.value.title,
        ...trayAccessPayload(form.value),
        managerUserUuids,
        description,
        ...media.createPayload(),
      });

      traysStore.invalidate();
      router.push({ name: 'tray-view', params: trayRouteParams(response.data) });
    } catch (error) {
      if (error.errors) {
        fieldErrors.value = error.errors;
      }
      errorMessage.value = error.message || 'Failed to create tray. Please try again.';
      scrollToFirstFormError('#tray-form');
    } finally {
      isSubmitting.value = false;
    }
  };

  return {
    form,
    isSubmitting,
    errorMessage,
    fieldErrors,
    editorRef,
    excludedManagerUuids,
    selectedManagerUsers,
    handleSubmit,
    handleMediaAltChange,
  };
}
